import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DashboardShell from '../../components/ui/DashboardShell';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { authHelpers, db } from '../../firebaseClient';
import { doc, getDoc } from 'firebase/firestore';
import { getUserOrgId, linkPendingInvitesForUser } from '../../utils/orgHelpers';
import { getCourse } from '../../utils/courseHelpers';
import { getNesolearnId } from '../../utils/nesolearnIdHelpers';
import { ensurePublicProfile } from '../../utils/learnerProfileHelpers';
import { listUserEnrollments } from '../../utils/enrollmentHelpers';
import { listCatalogListings } from '../../utils/catalogHelpers';
import ProfileSummaryCard from './components/ProfileSummaryCard';
import ContinueLearningCard from './components/ContinueLearningCard';
import CourseProgressCard from './components/CourseProgressCard';
import RecommendedCoursesSection from './components/RecommendedCoursesSection';
import ClaimRewardModal from './components/ClaimRewardModal';
import LiveNowBanner from './components/LiveNowBanner';
import ProfileCard from '../../components/profile-card/ProfileCard';
import { getStreakState, claimStreakReward, getTierInfo } from '../../utils/streakHelpers';
import { getLearnerActivity } from '../../utils/activityHelpers';
import { subscribeIsMentorAnywhere, subscribeMyMentors, getTodaysConfirmedAppointment } from '../../utils/mentorshipHelpers';
import { listPendingAssessmentsForLearner, listLearnerSkillBreakdown } from '../../utils/assessmentHelpers';
import { listLearnerLearningEvents } from '../../utils/learningEventHelpers';
import TodaysFocusCard from './components/TodaysFocusCard';
import LearningIntelligenceCard from './components/LearningIntelligenceCard';
import LearningPathsSection from './components/LearningPathsSection';
import StatsStrip from './components/StatsStrip';
import { listUserPathEnrollments, createPathEnrollment, computePathProgress } from '../../utils/pathEnrollmentHelpers';

// Maps a raw activity log entry (from learnerPoints.activity) to the shape
// ProfileSummaryCard's achievement chips expect.
const formatActivity = (activity) =>
  (activity || [])?.map((entry) => ({
    id: entry?.id,
    name: entry?.label,
    icon: entry?.icon || 'Sparkles',
    color: entry?.color || 'text-accent',
  }));

const findNextLesson = (course, completedLessonIds) => {
  const flat = [];
  course?.modules?.forEach((m) => m?.lessons?.forEach((l) => flat.push(l)));
  return flat?.find((l) => !completedLessonIds?.includes(l?.id)) || null;
};

const ProgressRing = ({ percent }) => {
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="-rotate-90">
      <circle cx="32" cy="32" r={radius} fill="none" stroke="currentColor" strokeWidth="5" className="text-white/20" />
      <circle
        cx="32"
        cy="32"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="text-white transition-all duration-700 ease-out"
      />
    </svg>
  );
};

// Segmented 7-day streak ring — one pill-shaped arc per day, filled as the
// user logs in on consecutive days.
const StreakRing = ({ completed = 0, total = 7, size = 88 }) => {
  const radius = size / 2 - 8;
  const center = size / 2;
  const segmentAngle = 360 / total;
  const gap = 8;

  const polarToCartesian = (angleDeg) => {
    const angleRad = ((angleDeg - 90) * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(angleRad),
      y: center + radius * Math.sin(angleRad),
    };
  };

  const describeArc = (startAngle, endAngle) => {
    const start = polarToCartesian(startAngle);
    const end = polarToCartesian(endAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {Array.from({ length: total }, (_, i) => {
        const startAngle = i * segmentAngle + gap / 2;
        const endAngle = (i + 1) * segmentAngle - gap / 2;
        // Fraction of this segment that's filled — 0 (not reached), 0–1 (currently
        // filling, mid-animation), or 1 (fully done). pathLength=1 lets us express
        // the fill purely as a 0–1 fraction regardless of the arc's real length.
        const fillFraction = Math.max(0, Math.min(1, completed - i));
        return (
          <path
            key={i}
            d={describeArc(startAngle, endAngle)}
            fill="none"
            stroke={fillFraction > 0 ? 'currentColor' : 'rgba(120,120,120,0.22)'}
            strokeWidth={6}
            strokeLinecap="round"
            pathLength={1}
            strokeDasharray={`${fillFraction} 1`}
          />
        );
      })}
    </svg>
  );
};

// Kaggle-style heading widget: small label on top, animated icon and value centered below.
const HeroStat = ({ label, value, unit, lottie }) => (
  <div className="flex flex-col items-center min-w-[88px]">
    <p className="text-sm font-medium text-foreground mb-3">{label}</p>
    <div className="h-24 flex flex-col items-center justify-center">
      <div className="w-8 h-8">
        <DotLottieReact src={lottie} loop autoplay style={{ width: '100%', height: '100%' }} />
      </div>
      <p className="mt-1.5 text-xl leading-none text-foreground">{value}</p>
      {unit && <p className="mt-1 text-xs text-muted-foreground">{unit}</p>}
    </div>
  </div>
);

const UserDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [recommendedListings, setRecommendedListings] = useState([]);
  const [streak, setStreak] = useState(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [animatedStreakDays, setAnimatedStreakDays] = useState(0);
  const [isMentor, setIsMentor] = useState(false);
  const [myMentors, setMyMentors] = useState([]);
  const [todaysAppointment, setTodaysAppointment] = useState(null);
  const [pendingAssessmentsCount, setPendingAssessmentsCount] = useState(0);
  const [learningEvents, setLearningEvents] = useState([]);
  const [skillBreakdown, setSkillBreakdown] = useState([]);
  const [pathListings, setPathListings] = useState([]);
  const [myPathEnrollments, setMyPathEnrollments] = useState([]);
  const [enrollingPathId, setEnrollingPathId] = useState(null);
  const heroCardRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Animates the streak ring counting up from 0 to the user's current day,
  // replayed every time the hero card scrolls into view (not just once on load).
  useEffect(() => {
    if (!streak || !heroCardRef.current) return;

    const target = streak.daysCompleted || 0;
    const runCountUpAnimation = () => {
      cancelAnimationFrame(animationFrameRef.current);
      const duration = 1100;
      const startTime = performance.now();

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        setAnimatedStreakDays(target * eased);
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(step);
        }
      };

      setAnimatedStreakDays(0);
      animationFrameRef.current = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) runCountUpAnimation();
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(heroCardRef.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [streak, loading]);
  const loadEnrolledCourses = async (userId) => {
    const { enrollments } = await listUserEnrollments(userId);
    const withCourses = await Promise.all(
      (enrollments || [])?.map(async (enrollment) => {
        const { course } = await getCourse(enrollment?.orgId, enrollment?.courseId);
        if (!course) return null;
        const nextLesson = findNextLesson(course, enrollment?.completedLessonIds || []);
        return { enrollment, course, nextLesson };
      })
    );
    const validEnrolled = withCourses?.filter(Boolean);
    setEnrolledCourses(validEnrolled);
    return validEnrolled;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { session, error: sessionError } = await authHelpers.getSession();

        if (sessionError || !session) {
          navigate('/authentication-portal');
          return;
        }

        const currentUser = session.user;

        await linkPendingInvitesForUser(currentUser.uid, currentUser.email).catch(() => {});
        const { orgId } = await getUserOrgId(currentUser.uid);
        if (orgId) {
          navigate('/organization-dashboard', { replace: true });
          return;
        }

        // Pull any saved profile overrides (name/email/avatar) written by the
        // edit-profile modal, so an uploaded photo or renamed profile
        // survives a page refresh instead of falling back to Auth defaults.
        let profileDoc = null;
        try {
          const profileSnap = await getDoc(doc(db, 'users', currentUser.uid));
          if (profileSnap.exists()) profileDoc = profileSnap.data();
        } catch (profileErr) {
          console.error('Error loading saved profile:', profileErr);
        }

        setUser({
          id: currentUser.uid,
          name: profileDoc?.name || currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
          email: profileDoc?.email || currentUser.email,
          avatar:
            profileDoc?.avatar ||
            currentUser.photoURL ||
            'https://pub-b365ff8e20e448c3a7257b35173d9a38.r2.dev/userrrrr.png',
          level: 1,
          currentStreak: 0,
          joinDate: currentUser.metadata?.creationTime
            ? new Date(currentUser.metadata.creationTime).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
            : 'Recently',
          totalLearningTime: '0h 0m',
        });

        // Self-heal: keep the public profile (learnerProfiles/{uid}) synced
        // with the latest name + resolved Nesolearn ID on every dashboard
        // visit. Fire-and-forget â€” never blocks or breaks the dashboard.
        // This is what powers /id/:nesolearnId.
        const { nesolearnId: resolvedNesolearnId } = await getNesolearnId(currentUser.uid);
        if (resolvedNesolearnId) {
          setUser((prev) => (prev ? { ...prev, nesolearnId: resolvedNesolearnId } : prev));
        }
        ensurePublicProfile({
          uid: currentUser.uid,
          name: profileDoc?.name || currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
          nesolearnId: resolvedNesolearnId || null,
          createdAt: profileDoc?.createdAt || null,
        }).catch(() => {});

        let streakState = null;
        try {
          streakState = await getStreakState(currentUser.uid);
          setStreak(streakState);
          const activity = await getLearnerActivity(currentUser.uid);
          setAchievements(formatActivity(activity));
        } catch (streakErr) {
          console.error('Error loading streak:', streakErr);
        }

        const validEnrolled = await loadEnrolledCourses(currentUser.uid);

        // Second self-heal pass: sync the Identity-card stats that weren't
        // known at the first ensurePublicProfile() call above. Fire-and-forget.
        const completedCourseCount =
          validEnrolled?.filter((e) => (e?.enrollment?.progressPercent || 0) >= 100)?.length || 0;
        ensurePublicProfile({
          uid: currentUser.uid,
          coursesCompleted: completedCourseCount,
          currentStreak: streakState?.daysCompleted ?? 0,
        }).catch(() => {});

        const { listings } = await listCatalogListings();
        const enrolledCourseIds = new Set(validEnrolled?.map((e) => e?.enrollment?.courseId));
        setRecommendedListings(
          (listings || [])?.filter((l) => l?.listingType !== 'path' && !enrolledCourseIds.has(l?.courseId))?.slice(0, 4)
        );
        setPathListings((listings || [])?.filter((l) => l?.listingType === 'path'));

        const { pathEnrollments } = await listUserPathEnrollments(currentUser.uid);
        setMyPathEnrollments(pathEnrollments || []);
      } catch (err) {
        console.error('Error loading dashboard:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

        fetchUserData();
  }, [navigate]);

    useEffect(() => {
    if (!user?.id) return;
    return subscribeIsMentorAnywhere(user.id, setIsMentor);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    return subscribeMyMentors(user.id, setMyMentors);
  }, [user?.id]);

  useEffect(() => {
    if (!myMentors?.length) {
      setTodaysAppointment(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        myMentors.map(async (m) => {
          const appt = await getTodaysConfirmedAppointment(m.orgId, m.id).catch(() => null);
          return appt ? { ...appt, mentorshipId: m.id } : null;
        })
      );
      if (!cancelled) setTodaysAppointment(results.find(Boolean) || null);
    })();
    return () => { cancelled = true; };
  }, [myMentors]);

  useEffect(() => {
    if (!user?.id || !enrolledCourses?.length) {
      setPendingAssessmentsCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const orgIds = enrolledCourses.map((e) => e?.enrollment?.orgId).filter(Boolean);
      const { assessments } = await listPendingAssessmentsForLearner(orgIds, user.id);
      if (!cancelled) setPendingAssessmentsCount(assessments?.length || 0);
    })();
    return () => { cancelled = true; };
  }, [user?.id, enrolledCourses]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const [{ events }, { skills }] = await Promise.all([
        listLearnerLearningEvents(user.id),
        listLearnerSkillBreakdown(user.id),
      ]);
      if (!cancelled) {
        setLearningEvents(events || []);
        setSkillBreakdown(skills || []);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const continueLearningEntry = useMemo(() => {
    const inProgress = enrolledCourses?.filter((e) => (e?.enrollment?.progressPercent || 0) < 100);
    if (inProgress?.length === 0) return null;
    return [...inProgress]?.sort((a, b) => {
      const aTime = a?.enrollment?.lastActivityAt?.toMillis?.() || 0;
      const bTime = b?.enrollment?.lastActivityAt?.toMillis?.() || 0;
      return bTime - aTime;
    })?.[0];
  }, [enrolledCourses]);

  const enrollmentByCourseId = useMemo(() => {
    const map = {};
    enrolledCourses?.forEach((e) => { map[e?.enrollment?.courseId] = e?.enrollment; });
    return map;
  }, [enrolledCourses]);

  const myPathsWithProgress = useMemo(() => {
    return myPathEnrollments.map((pe) => ({
      ...pe,
      progress: computePathProgress(pe?.nodeCourseIds, enrollmentByCourseId),
    }));
  }, [myPathEnrollments, enrollmentByCourseId]);

  const browsablePathListings = useMemo(() => {
    const myPathIds = new Set(myPathEnrollments.map((pe) => pe.pathId));
    return pathListings.filter((l) => !myPathIds.has(l.pathId));
  }, [pathListings, myPathEnrollments]);

  const overallProgress = useMemo(() => {
    if (enrolledCourses?.length === 0) return 0;
    const total = enrolledCourses?.reduce((sum, e) => sum + (e?.enrollment?.progressPercent || 0), 0);
    return Math.round(total / enrolledCourses?.length);
  }, [enrolledCourses]);

  const handleEnrollInPath = async (listing) => {
    if (!user?.id || enrollingPathId) return;
    setEnrollingPathId(listing.pathId);
    const { pathEnrollment, error: enrollError } = await createPathEnrollment({
      userId: user.id,
      pathId: listing.pathId,
      orgId: listing.providerId,
      listing,
    });
    if (!enrollError && pathEnrollment) {
      setMyPathEnrollments((prev) => [...prev, pathEnrollment]);
      await loadEnrolledCourses(user.id); // node courses were just auto-enrolled — refresh
    }
    setEnrollingPathId(null);
  };

  const handleProfileUpdate = (updates) => {
    setUser((prev) => ({
      ...prev,
      name: updates?.name ?? prev?.name,
      email: updates?.email ?? prev?.email,
      avatar: updates?.avatar ?? prev?.avatar,
    }));
  };

  const handleClaim = async () => {
    if (!user?.id || isClaiming) return;
    setIsClaiming(true);
    const { error, pointsAwarded, newTier } = await claimStreakReward(user.id);
    setIsClaiming(false);
    if (!error) {
      const newTierInfo = getTierInfo(newTier);
      setStreak((prev) => ({ ...prev, currentTier: newTier, daysCompleted: 0, claimAvailable: false }));
      setClaimResult({ pointsAwarded, tierName: newTierInfo.name, tierIcon: newTierInfo.icon });
      const refreshedActivity = await getLearnerActivity(user.id);
      setAchievements(formatActivity(refreshedActivity));
    }
  };

  const handleSignOut = async () => {
    await authHelpers.signOut?.();
    navigate('/authentication-portal');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertTriangle" size={28} className="text-error" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const stats = {
    totalLearningTime: user?.totalLearningTime,
    coursesCompleted: enrolledCourses?.filter((e) => (e?.enrollment?.progressPercent || 0) >= 100)?.length || 0,
    certificatesEarned: 0,
    currentStreak: streak?.daysCompleted || 0,
  };

  const firstName = user?.name?.split(' ')?.[0];
  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  })();

  const notifications = [
    streak?.claimAvailable && {
      id: 'reward',
      icon: 'Gift',
      title: 'Reward ready to claim',
      description: 'Your weekly streak reward is ready. Claim it from your heading.',
      onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    },
    pendingAssessmentsCount > 0 && {
      id: 'assessments',
      icon: 'ClipboardList',
      title: 'Assessments waiting',
      description: `You have ${pendingAssessmentsCount} pending assessment${pendingAssessmentsCount === 1 ? '' : 's'}.`,
    },
    todaysAppointment && {
      id: 'mentor-today',
      icon: 'HeartHandshake',
      title: 'Mentor session today',
      description: 'You have a confirmed session with your mentor today.',
    },
  ].filter(Boolean);

  const scrollToId = (id, fallbackRoute) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else navigate(fallbackRoute);
  };

  const statTiles = [
    { label: 'Courses', icon: 'BookOpen', lottie: 'https://lottie.host/cab59dce-0441-4a35-8951-9f81a7b3f83e/VAogBPY6Dq.lottie', tint: 'bg-green-50/60', value: enrolledCourses?.length || 0, caption: 'enrolled', onClick: scrollToId('your-courses', '/course-catalog') },
    { label: 'Notebooks', icon: 'Code', value: 0, caption: 'total created', soon: true },
    { label: 'Projects', icon: 'FolderOpen', value: 0, caption: 'total submitted', soon: true },
    { label: 'Challenges', icon: 'Trophy', value: 0, caption: 'total joined', soon: true },
    { label: 'Community', icon: 'Users', value: 0, caption: 'total posts', soon: true },
    { label: 'Credentials', icon: 'Award', lottie: 'https://lottie.host/1a927f88-738a-4ca3-8436-53e359832800/zdvfNVZSaz.lottie', tint: 'bg-purple-50/60', value: stats.certificatesEarned, caption: 'total earned', soon: true },
  ];

  return (
    <DashboardShell user={user} onSignOut={handleSignOut} notifications={notifications}>

            <div className="space-y-4 sm:space-y-6">
        {/* Hero — bespoke dark editorial surface, no stock photography. Reuses the
            page's own ambient-blob language so the hero reads as one owned visual
            identity rather than a marketing-banner template. */}
        <div
          ref={heroCardRef}
className="relative pt-6"
        >
          <div className="absolute inset-0 overflow-hidden">
          </div>
          <style>{`
  @keyframes hero-rise { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes hero-drift-a { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(40px,24px) scale(1.12); } }
  @keyframes hero-drift-b { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-48px,-20px) scale(1.1); } }
  @keyframes hero-drift-c { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-24px,28px); } }
  @keyframes hero-breathe { 0%,100% { opacity: .45; } 50% { opacity: 1; } }
  .hero-rise { opacity: 0; animation: hero-rise 600ms cubic-bezier(.22,.8,.3,1) forwards; }
  .hero-blob-a { animation: hero-drift-a 16s ease-in-out infinite; }
  .hero-blob-b { animation: hero-drift-b 19s ease-in-out infinite; }
  .hero-blob-c { animation: hero-drift-c 22s ease-in-out infinite; }
  .hero-glow { animation: hero-breathe 3.6s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .hero-rise { opacity: 1; animation: none; }
    .hero-blob-a, .hero-blob-b, .hero-blob-c, .hero-glow { animation: none; }
  }
`}</style>

          <div className="relative flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6 text-center sm:text-left">
            <div className="flex-1 min-w-0">
              <p className="hero-rise text-muted-foreground text-xs sm:text-sm font-medium mb-1.5 tracking-wide" style={{ animationDelay: '80ms' }}>
                Welcome, {user?.name}!
              </p>
              <h1 className="text-2xl sm:text-3xl font-medium text-foreground mb-2 tracking-tight" style={{ animationDelay: '160ms' }}>
                Here's where your learning stands.
              </h1>
              <p className="hero-rise text-muted-foreground text-sm max-w-md mx-auto sm:mx-0" style={{ animationDelay: '240ms' }}>
                {enrolledCourses?.length === 0
                  ? 'Your journey starts with your first course.'
                  : `You're enrolled in ${enrolledCourses?.length} course${enrolledCourses?.length === 1 ? '' : 's'} — keep the momentum going.`}
              </p>
              <div className="hero-rise mt-4 flex flex-wrap justify-center sm:justify-start gap-3" style={{ animationDelay: '320ms' }}>
                {continueLearningEntry ? (
                  <Link
                    to={`/video-learning-interface?orgId=${continueLearningEntry?.enrollment?.orgId}&courseId=${continueLearningEntry?.enrollment?.courseId}${
                      continueLearningEntry?.nextLesson?.id ? `&lessonId=${continueLearningEntry?.nextLesson?.id}` : ''
                    }`}
                  >
                    <Button variant="default" iconName="Play" iconPosition="left">
                      Continue Learning
                    </Button>
                  </Link>
                ) : null}
                <Link to="/course-catalog">
                  <Button
                    variant={continueLearningEntry ? 'outline' : 'default'}
                    iconName="Compass"
                    iconPosition="left"
                  >
                    Browse Courses
                  </Button>
                </Link>
              </div>
            </div>

            <div
              className="hero-rise flex-shrink-0 flex items-start justify-center gap-6 sm:self-start"
              style={{ animationDelay: '180ms' }}
            >
              <HeroStat
                label="Learning time"
                value={stats.totalLearningTime}
                lottie="https://lottie.host/81ecf84d-b43e-4bae-a8e1-7a0b6b9829be/hu9h4u9cDp.lottie"
              />
            </div>

            <div className="hero-rise flex-shrink-0 flex flex-row sm:flex-col items-center justify-center gap-3 sm:gap-2 sm:self-start" style={{ animationDelay: '200ms' }}>
              {streak ? (
                <>
                  <p className="hidden sm:block text-sm font-medium text-foreground mb-1">Tier progress</p>
                  <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center text-warning">
                    <div className="hero-glow absolute inset-0 rounded-full bg-warning/20 blur-xl" />
                    <StreakRing completed={animatedStreakDays} />
                    <div className="absolute flex flex-col items-center">
                      <div className="w-7 h-7 -mb-0.5">
                    <DotLottieReact
                      src="https://lottie.host/8c11fecf-9ecb-44d9-b01c-4b9dfa80de2a/pnmPR4993a.lottie"
                      loop
                      autoplay
                      style={{ width: '100%', height: '100%' }}
                      />
                      </div>
                      <span className="text-sm sm:text-base font-bold text-foreground">
                        {Math.round(animatedStreakDays)}/7
                      </span>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-[11px] sm:text-xs font-semibold text-foreground uppercase tracking-wide">
                      {getTierInfo(streak.currentTier).name}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {getTierInfo(streak.currentTier).reward} NESOL / week
                    </p>
                  </div>
                  {streak.claimAvailable && (
                    <button
                      onClick={handleClaim}
                      disabled={isClaiming}
                      className="mt-1 flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-accent text-white text-xs font-semibold shadow-sm hover:bg-accent/90 transition-smooth disabled:opacity-60"
                    >
                      <Icon name="Gift" size={13} />
                      {isClaiming ? 'Claiming...' : 'Claim'}
                    </button>
                  )}
                </>
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-border animate-pulse" />
              )}
            </div>
          </div>
        </div>

                                        <StatsStrip tiles={statTiles} />

        <LiveNowBanner enrolledCourses={enrolledCourses} />

        {error && user && (
          <div className="p-4 bg-warning/10 backdrop-blur-md border border-warning/20 rounded-lg">
            <p className="text-sm text-warning">{error}</p>
          </div>
        )}

        <ProfileSummaryCard
          user={user}
          rankName={streak ? getTierInfo(streak.currentTier).name : undefined}
          streakDays={streak?.daysCompleted}
          achievements={achievements}
          onProfileUpdate={handleProfileUpdate}
        />

        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-base sm:text-lg font-semibold text-foreground">Your Nesolearn ID</h3>
            {user?.nesolearnId && (
              <Link to={`/id/${user.nesolearnId}`} className="text-xs font-medium text-accent hover:underline">
                View public profile
              </Link>
            )}
          </div>
          <ProfileCard
            compact
            profile={{
              name: user?.name,
              nesolearnId: user?.nesolearnId,
              joinDateLabel: user?.joinDate,
              credentials: [],
              coursesCompleted: stats.coursesCompleted,
              currentStreak: stats.currentStreak,
            }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {continueLearningEntry && (
            <ContinueLearningCard
              course={continueLearningEntry?.course}
              enrollment={continueLearningEntry?.enrollment}
              nextLesson={continueLearningEntry?.nextLesson}
            />
          )}
          <TodaysFocusCard
            continueLearningEntry={continueLearningEntry}
            todaysAppointment={todaysAppointment}
            pendingAssessmentsCount={pendingAssessmentsCount}
          />
        </div>

        <LearningIntelligenceCard events={learningEvents} skills={skillBreakdown} />

        <div id="learning-paths" className="scroll-mt-20">
          <LearningPathsSection
            myPaths={myPathsWithProgress}
            browsablePaths={browsablePathListings}
            onEnroll={handleEnrollInPath}
            enrollingPathId={enrollingPathId}
          />
        </div>

        {enrolledCourses?.length === 0 ? (
          <div className="bg-white/30 backdrop-blur-xl border border-dashed border-white/40 rounded-xl p-8 sm:p-10 text-center">
           <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-accent to-accent/40 p-[2px] mx-auto mb-4 shadow-[0_0_20px_rgba(99,102,241,0.25)]">
              <div className="w-full h-full rounded-full bg-background/95 flex items-center justify-center">
                <Icon name="Compass" size={26} className="text-accent" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No courses yet</h3>
            <p className="text-muted-foreground mb-5 max-w-sm mx-auto text-sm sm:text-base">
              Explore the catalog and enroll in your first course to see your progress here.
            </p>
            <Link to="/course-catalog">
              <Button variant="default" iconName="Compass" iconPosition="left">
                Browse Courses
              </Button>
            </Link>
          </div>
        ) : (
          <div id="your-courses" className="space-y-3 sm:space-y-4 scroll-mt-20">
            <div className="flex items-center gap-2 px-1">
              <span className="w-1.5 h-1.5 rounded-full bg-accent" />
              <h3 className="text-base sm:text-lg font-semibold text-foreground">Your Courses</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {enrolledCourses?.map(({ enrollment, course, nextLesson }) => (
                <CourseProgressCard
                  key={enrollment?.courseId}
                  course={course}
                  enrollment={enrollment}
                  nextLesson={nextLesson}
                />
              ))}
            </div>
          </div>
        )}

                <RecommendedCoursesSection listings={recommendedListings} />

        {(isMentor || myMentors?.length > 0) && (
          <div className="space-y-3">
            {isMentor && (
              <Link
                to="/mentor-workspace"
                className="flex items-center justify-between gap-3 bg-accent/10 border border-accent/20 rounded-xl px-4 py-3 hover:bg-accent/15 transition-smooth"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="HeartHandshake" size={18} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">You're mentoring learners</p>
                    <p className="text-xs text-muted-foreground">Open your mentor workspace to log sessions and add notes</p>
                  </div>
                </div>
                <Icon name="ChevronRight" size={18} className="text-muted-foreground flex-shrink-0" />
              </Link>
            )}

            {myMentors?.map((m) => (
              <Link
                key={m?.id}
                to={`/mentee-workspace?mentorshipId=${m?.id}`}
                className="flex items-center gap-3 bg-card border border-border rounded-xl px-4 py-3 hover:bg-muted/50 transition-smooth"
              >
                <div className="w-9 h-9 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="HeartHandshake" size={18} className="text-success" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{m?.mentorName} is mentoring you</p>
                  {m?.goal && <p className="text-xs text-muted-foreground">Goal: {m?.goal}</p>}
                </div>
                <Icon name="ChevronRight" size={18} className="text-muted-foreground flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>

      <ClaimRewardModal
        isOpen={!!claimResult}
        onClose={() => setClaimResult(null)}
        pointsAwarded={claimResult?.pointsAwarded}
        tierName={claimResult?.tierName}
        tierIcon={claimResult?.tierIcon}
      />
    </DashboardShell>
  );
};

export default UserDashboard;